<template>
  <div class="fatigue-index-card">
    <div class="fatigue-index-card__levels">
      <div
          v-for="level in normalizedLevels"
          :key="level.key"
          class="fatigue-index-card__level-row"
      >
        <div class="fatigue-index-card__level-name">
          <span
              class="fatigue-index-card__mark"
              :style="{ backgroundColor: level.color }"
          ></span>

          <span>{{ level.label }}</span>
        </div>

        <span
            class="fatigue-index-card__change"
            :class="getChangeClass(level)"
        >
          {{ getPercentChangeText(level) }}
        </span>

        <span class="fatigue-index-card__percent">
          {{ formatLevelValue(level.value) }}
        </span>
      </div>
    </div>

    <div
        class="fatigue-index-card__bar"
        :class="{ 'fatigue-index-card__bar--empty': !hasLevelSegments }"
        :style="barGridStyle"
    >
      <template v-if="hasLevelSegments">
        <div
            v-for="(level, index) in levelSegments"
            :key="level.key"
            class="fatigue-index-card__bar-part"
            :style="getBarPartStyle(index)"
        ></div>
      </template>
    </div>

    <div
        v-if="hasLevelSegments"
        class="fatigue-index-card__bar-values"
        :style="barGridStyle"
    >
      <span
          v-for="level in levelSegments"
          :key="level.key"
          class="fatigue-index-card__bar-value"
      >
        {{ formatLevelValue(level.value) }}
      </span>
    </div>

    <div class="fatigue-index-card__positions">
      <div class="fatigue-index-card__subtitle">
        Хроническая усталость по должностям
      </div>


      <div class="fatigue-index-card__position-list">
        <div
            v-for="position in normalizedPositions"
            :key="position.name"
            class="fatigue-index-card__position-row"
        >
          <div class="fatigue-index-card__position-name">
            <span class="fatigue-index-card__position-title">
              {{ position.name }}
            </span>

            <a-popover
                v-if="position.sourceWarning"
                trigger="hover"
                placement="top"
            >
              <template #content>
                <div class="fatigue-index-card__popover">
                  {{ position.sourceWarning }}
                </div>
              </template>

              <InfoCircleOutlined class="fatigue-index-card__warning-icon" />
            </a-popover>
          </div>

          <div class="fatigue-index-card__mini-scale">
            <div class="fatigue-index-card__mini-steps">
              <span
                  v-for="step in getMiniScaleSteps(position)"
                  :key="step.index"
                  class="fatigue-index-card__mini-step"
                  :style="{ backgroundColor: step.color }"
              ></span>
            </div>
          </div>

          <div class="fatigue-index-card__position-value">
            <span class="current-position-value">{{ position.value }}</span>
            <span>/ {{ position.max }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue'
import { InfoCircleOutlined } from '@ant-design/icons-vue'

const GREY_COLOR = '#D1D1D1'
const EMPTY_STEP_COLOR = '#EEF0F3'
const COLOR_CLASS_BY_NAME = {
  green: 'positive-change',
  red: 'negative-change',
  neutral: 'neutral-change',
}

const DEFAULT_LEVELS = [
  {
    key: 'low',
    label: 'Низкий уровень',
    change: 0,
    value: 0,
    color: '#21AC0C',
  },
  {
    key: 'moderate',
    label: 'Умеренный',
    change: 0,
    value: 0,
    color: '#99DA6E',
  },
  {
    key: 'high',
    label: 'Высокий',
    change: 0,
    value: 0,
    color: '#FF9F40',
  },
  {
    key: 'critical',
    label: 'Критический',
    change: 0,
    value: 0,
    color: '#F55565',
  },
]

const LEVEL_RANGES = [
  {
    color: '#21AC0C',
  },
  {
    color: '#99DA6E',
  },
  {
    color: '#FF9F43',
  },
  {
    color: '#F55565',
  },
]

export default {
  name: 'ChronicFatigueIndexCard',

  components: {
    InfoCircleOutlined,
  },

  props: {
    levels: {
      type: Array,
      default: () => [],
    },
    positions: {
      type: Array,
      default: () => [],
    },
  },

  setup(props) {
    const toNumber = (value) => {
      const number = Number(value)
      return Number.isFinite(number) ? number : 0
    }

    const roundToOne = (value) => {
      return Math.round(toNumber(value) * 10) / 10
    }

    const formatPercent = (value) => {
      const normalized = roundToOne(value)

      return Number.isInteger(normalized)
          ? `${normalized}%`
          : `${normalized.toFixed(1)}%`
    }

    const formatValue = (value) => {
      const normalized = roundToOne(value)

      return Number.isInteger(normalized)
          ? `${normalized}`
          : normalized.toFixed(1)
    }

    const getPositionColorByValue = (value, max = 10) => {
      const safeValue = toNumber(value)
      const safeMax = toNumber(max) || 10
      const ratio = safeValue / safeMax

      if (ratio <= 0.2) {
        return '#21AC0C'
      }

      if (ratio <= 0.5) {
        return '#99DA6E'
      }

      if (ratio <= 0.8) {
        return '#FF9F43'
      }

      return '#F55565'
    }

    const normalizedLevels = computed(() => {
      const sourceLevels = Array.isArray(props.levels) && props.levels.length
          ? props.levels
          : DEFAULT_LEVELS

      return sourceLevels.map((level, index) => {
        const value = roundToOne(level.value)
        const range = LEVEL_RANGES[index]

        return {
          ...level,
          key: level?.key || level?.label,
          label: level?.label || '',
          change: roundToOne(level.change),
          direction: level?.direction || '',
          changeColor: level?.changeColor || '',
          value,
          color: range?.color || level.color,
        }
      })
    })

    const levelSegments = computed(() => {
      const activeSegments = normalizedLevels.value
          .map((level, index) => ({
            ...level,
            color: LEVEL_RANGES[index]?.color || level.color,
            ratio: level.value,
          }))
          .filter((level) => level.value > 0)

      if (activeSegments.length) {
        return activeSegments
      }

      return [
        {
          key: 'empty',
          value: 0,
          color: GREY_COLOR,
          ratio: 1,
        },
      ]
    })

    const hasLevelSegments = computed(() => {
      return levelSegments.value.length > 0
    })

    const hasChartData = computed(() => {
      const hasLevelValues = Array.isArray(props.levels) &&
          props.levels.some((level) => roundToOne(level?.value) > 0)
      const hasPositionValues = Array.isArray(props.positions) &&
          props.positions.some((position) => toNumber(position?.value) > 0)

      return hasLevelValues || hasPositionValues
    })

    const formatLevelValue = (value) => {
      return hasChartData.value
          ? formatPercent(value)
          : formatValue(value)
    }

    const barGridStyle = computed(() => {
      if (!levelSegments.value.length) {
        return {}
      }

      return {
        gridTemplateColumns: levelSegments.value
            .map((level) => `${level.ratio}fr`)
            .join(' '),
      }
    })

    const getBarPartStyle = (index) => {
      const isFirst = index === 0
      const isLast = index === levelSegments.value.length - 1
      const currentLevel = levelSegments.value[index]

      return {
        backgroundColor: currentLevel.color,
        borderTopLeftRadius: isFirst ? '999px' : '0',
        borderBottomLeftRadius: isFirst ? '999px' : '0',
        borderTopRightRadius: isLast ? '999px' : '0',
        borderBottomRightRadius: isLast ? '999px' : '0',
      }
    }

    const normalizedPositions = computed(() => {
      return props.positions.map((position) => {
        const max = toNumber(position.max) || 10
        const rawValue = toNumber(position.value)
        const value = Math.max(0, Math.min(rawValue, max))
        const color = getPositionColorByValue(value, max)

        return {
          ...position,
          sourceWarning: position.warning || '',
          value,
          max,
          color,
          warning: value > 5
              ? 'Рекомендуется снизить нагрузку на специалистов'
              : '',
        }
      })
    })

    const getMiniScaleSteps = (position) => {
      const totalSteps = 10
      const filledSteps = Math.round((position.value / position.max) * totalSteps)

      return Array.from({ length: totalSteps }, (_, index) => ({
        index,
        color: index < filledSteps ? position.color : EMPTY_STEP_COLOR,
      }))
    }

    const getChangeClass = (level) => {
      const colorClass = COLOR_CLASS_BY_NAME[String(level?.changeColor || '').toLowerCase()]

      if (colorClass) {
        return colorClass
      }

      const change = roundToOne(level?.change)

      if (change > 0) {
        return 'positive-change'
      }

      if (change < 0) {
        return 'negative-change'
      }

      return 'neutral-change'
    }

    const getPercentChangeText = (value) => {
      const normalized = roundToOne(value?.change ?? value)

      if (normalized > 0) {
        return `↑ ${normalized}%`
      }

      if (normalized < 0) {
        return `↓ ${Math.abs(normalized)}%`
      }

      return '–'
    }

    return {
      normalizedLevels,
      levelSegments,
      hasLevelSegments,
      hasChartData,
      barGridStyle,
      getBarPartStyle,
      normalizedPositions,
      getMiniScaleSteps,
      getChangeClass,
      getPercentChangeText,
      formatLevelValue,
    }
  },
}
</script>

<style scoped lang="scss">
.fatigue-index-card {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  text-align: start;
}

.fatigue-index-card__levels {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 16px;
}

.fatigue-index-card__level-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(30px, 40px) minmax(30px, 60px);
  align-items: center;
  column-gap: 10px;
  min-width: 0;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.65);
}

.fatigue-index-card__level-name {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.65);
}

.fatigue-index-card__level-name span:last-child {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fatigue-index-card__mark {
  flex: 0 0 auto;
  width: 10px;
  height: 10px;
  border-radius: 2px;
}

.fatigue-index-card__change {
  min-width: 0;
  text-align: right;
  white-space: nowrap;
}

.fatigue-index-card__percent {
  min-width: 50px;
  text-align: right;
  white-space: nowrap;
  color: rgba(0, 0, 0, 0.88);
}

.fatigue-index-card__bar {
  display: grid;
  width: 100%;
  height: 8px;
  gap: 2px;
}

.fatigue-index-card__bar--empty {
  display: flex;
  background: #D9DEE7;
  border-radius: 999px;
}

.fatigue-index-card__bar-part {
  height: 100%;
  min-width: 0;
}

.fatigue-index-card__bar-values {
  display: grid;
  width: 100%;
  margin-top: 4px;
  gap: 2px;
  font-size: 11px;
  line-height: 110%;
  color: rgba(0, 0, 0, 0.45);
}

.fatigue-index-card__bar-value {
  min-width: 0;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fatigue-index-card__positions {
  position: relative;
  margin-top: 20px;
  min-height: 118px;
}

.fatigue-index-card__subtitle {
  margin-bottom: 16px;
  font-weight: 600;
  font-size: 16px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.fatigue-index-card__position-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.fatigue-index-card__position-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  align-items: center;
  column-gap: 16px;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.65);
}

.fatigue-index-card__position-name {
  display: inline-flex;
  align-items: center;
  min-width: 0;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
}

.fatigue-index-card__position-title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.fatigue-index-card__warning-icon {
  margin-left: 8px;
  color: #FF9F43;
  font-size: 13px;
  flex: 0 0 auto;
  cursor: pointer;
}

.fatigue-index-card__popover {
  max-width: 240px;
  font-size: 13px;
  line-height: 130%;
}

.fatigue-index-card__mini-scale {
  width: 38px;
  min-width: 38px;
}

.fatigue-index-card__mini-steps {
  display: grid;
  grid-template-columns: repeat(10, 2px);
  column-gap: 2px;
  align-items: center;
  justify-content: start;
  width: 38px;
  height: 8px;
}

.fatigue-index-card__mini-step {
  display: block;
  width: 2px;
  height: 8px;
  border-radius: 999px;
  background: #EEF0F3;
}

.fatigue-index-card__position-value {
  display: flex;
  justify-content: flex-end;
  gap: 2px;
  min-width: 52px;
  white-space: nowrap;
}

.fatigue-index-card__position-value span:first-child {
  color: rgba(0, 0, 0, 0.88);
}

.fatigue-index-card__position-value span:last-child {
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.45);
}

.fatigue-index-card__empty {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;

  padding: 6px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  background: #fff;
  font-weight: 400;
  font-size: 12px;
  line-height: 110%;
  text-align: center;
  white-space: nowrap;
}

.positive-change {
  color: var(--main-green);
}

.negative-change {
  color: #F55565;
}

.neutral-change {
  color: rgba(0, 0, 0, 0.45);
}

@media (max-width: $mobile-breakpoint) {
  .fatigue-index-card__position-row {
    grid-template-columns: minmax(0, 1fr) auto;
    row-gap: 4px;
    column-gap: 16px;
  }

  .fatigue-index-card__mini-scale {
    grid-column: 1;
  }

  .fatigue-index-card__position-value {
    grid-column: 2;
    grid-row: 1 / span 2;
  }
}

.current-position-value {
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 120%;
}
</style>
